import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { masterSupabase, initializeTenantClient, getUserRole } from "./lib/supabase.js";
import { isCustomDomain, resolveCustomDomain, getResolvedDomain } from "./lib/domain.js";
import Login from "./pages/Login.jsx";
import SetPassword from "./pages/SetPassword.jsx";
import Overview from "./pages/Overview.jsx";
import Documents from "./pages/Documents.jsx";
import Locations from "./pages/Locations.jsx";
import Products from "./pages/Products.jsx";
import Conversations from "./pages/Conversations.jsx";
import Leads from "./pages/Leads.jsx";
import Widget from "./pages/Widget.jsx";
import Analytics from "./pages/Analytics.jsx";
import Reports from "./pages/Reports.jsx";
import Agents from "./pages/Agents.jsx";
import Settings from "./pages/Settings.jsx";
import Billing from "./pages/Billing.jsx";
import Team from "./pages/Team.jsx";
import DocumentWidget from "./pages/DocumentWidget.jsx";
import FloorAccess from "./pages/FloorAccess.jsx";
import Training from "./pages/TrainingInterface.jsx";
import ApiDocs from "./pages/ApiDocs.jsx";

// ─── Role context ───
const UserRoleContext = createContext({ role: "viewer", userId: null });
export function useUserRole() { return useContext(UserRoleContext); }

// ─── Domain context — provides custom domain info to Login and other components ───
const DomainContext = createContext({ isCustom: false, domainInfo: null });
export function useDomain() { return useContext(DomainContext); }

const ROLE_PERMISSIONS = {
    owner:  { manageTeam: true, manageBilling: true, manageApiKeys: true, manageSettings: true, editContent: true, viewContent: true, viewConversations: true, viewAnalytics: true, trainAssistant: true },
    admin:  { manageTeam: true, manageBilling: false, manageApiKeys: false, manageSettings: true, editContent: true, viewContent: true, viewConversations: true, viewAnalytics: true, trainAssistant: true },
    editor: { manageTeam: false, manageBilling: false, manageApiKeys: false, manageSettings: false, editContent: true, viewContent: true, viewConversations: true, viewAnalytics: true, trainAssistant: true },
    viewer: { manageTeam: false, manageBilling: false, manageApiKeys: false, manageSettings: false, editContent: false, viewContent: true, viewConversations: true, viewAnalytics: true, trainAssistant: false },
    // Sales: Training (with live preview), Analytics, and everything
    // conversational — nothing else. A focused cockpit, not a smaller admin.
    sales:  { manageTeam: false, manageBilling: false, manageApiKeys: false, manageSettings: false, editContent: false, viewContent: false, viewConversations: true, viewAnalytics: true, trainAssistant: true },
};
export function RequirePerm({ perm, children }) {
  const { role } = useUserRole();
  if (!hasPermission(role, perm)) return <Navigate to={hasPermission(role, "viewContent") ? "/" : "/training"} replace />;
  return children;
}

export function hasPermission(role, permission) {
    return ROLE_PERMISSIONS[role]?.[permission] ?? false;
}

// Auth-protected admin app
function AdminApp() {
    const [session, setSession] = useState(undefined);
    const [account, setAccount] = useState(null);
    const [userRole, setUserRole] = useState("viewer");
    const [userId, setUserId] = useState(null);
    const [error, setError] = useState(null);
    const [needsPassword, setNeedsPassword] = useState(false);

    useEffect(() => {
        const { data: { subscription } } = masterSupabase.auth.onAuthStateChange(async (event, session) => {
            console.log("AUTH EVENT:", event, "hash:", window.location.hash);
            setSession(session);
            if (session) {
                try {
                    setUserId(session.user.id);

                    // ─── Try primary path: initializeTenantClient (queries by auth_user_id) ───
                    let acct = await initializeTenantClient(session);

                    // ─── Fallback: if no account found, try matching by email and backfill auth_user_id ───
                    if (!acct && session.user?.email) {
                        console.log("INIT: primary lookup failed, trying email fallback for:", session.user.email);
                        const masterUrl = import.meta.env.VITE_SUPABASE_URL;
                        const masterAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

                        const res = await fetch(
                            `${masterUrl}/rest/v1/account_users?select=id,account_id,email,auth_user_id&email=eq.${encodeURIComponent(session.user.email.toLowerCase())}&is_active=eq.true`,
                            {
                                headers: {
                                    "apikey": masterAnonKey,
                                    "Authorization": `Bearer ${session.access_token}`,
                                },
                            }
                        );
                        const rows = await res.json();
                        console.log("INIT: email fallback rows =", rows);

                        const accountUser = rows?.[0];
                        if (accountUser?.id) {
                            // Backfill auth_user_id so future logins work via the primary path
                            console.log("INIT: backfilling auth_user_id for account_users row:", accountUser.id);
                            await masterSupabase
                                .from("account_users")
                                .update({ auth_user_id: session.user.id })
                                .eq("id", accountUser.id);

                            // Re-run initializeTenantClient now that auth_user_id is set
                            acct = await initializeTenantClient(session);
                        }
                    }

                    console.log("ACCOUNT RESULT:", acct);
                    setAccount(acct);

                    // ─── Role comes from initializeTenantClient (same fetch, no extra query) ───
                    if (acct) {
                        const role = getUserRole();
                        console.log("USER ROLE:", role);
                        setUserRole(role);
                    }

                    // ─── Detect if user needs to set/reset a password ───
                    // Two cases:
                    //   1. First-time invite: accepted_at is null (any SIGNED_IN event, not page reload)
                    //   2. Password reset: PASSWORD_RECOVERY event (user clicked a reset link)
                    let showPasswordSetup = false;

                    if (event === "PASSWORD_RECOVERY") {
                        // Always show SetPassword on a reset link click
                        console.log("PASSWORD_RECOVERY event — showing password setup");
                        showPasswordSetup = true;
                        setNeedsPassword(true);
                    } else if (acct && session.user?.id && event !== "INITIAL_SESSION") {
                        // Check if this is a first-time invite acceptance
                        const masterUrl = import.meta.env.VITE_SUPABASE_URL;
                        const masterAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
                        const checkRes = await fetch(
                            `${masterUrl}/rest/v1/account_users?select=accepted_at&auth_user_id=eq.${session.user.id}&is_active=eq.true&limit=1`,
                            {
                                headers: {
                                    "apikey": masterAnonKey,
                                    "Authorization": `Bearer ${session.access_token}`,
                                },
                            }
                        );
                        const checkRows = await checkRes.json();
                        const userRow = checkRows?.[0];
                        console.log("INVITE CHECK: accepted_at =", userRow?.accepted_at, "event =", event);

                        if (userRow && !userRow.accepted_at) {
                            showPasswordSetup = true;
                            setNeedsPassword(true);
                        }
                    }

                    // ─── Update login timestamps (skip if showing password setup) ───
                    if (session.user?.id && !showPasswordSetup) {
                        const now = new Date().toISOString();
                        masterSupabase.from("account_users")
                            .update({ accepted_at: now, last_login_at: now })
                            .eq("auth_user_id", session.user.id)
                            .is("accepted_at", null)
                            .then(() => {});
                        masterSupabase.from("account_users")
                            .update({ last_login_at: now })
                            .eq("auth_user_id", session.user.id)
                            .then(() => {});
                    }
                } catch (e) {
                    console.error("Init failed:", e);
                    setError(e.message);
                }
            } else {
                setAccount(null);
                setUserRole("viewer");
                setUserId(null);
            }
        });
        return () => subscription.unsubscribe();
    }, []);

    if (session === undefined) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "#555" }}>Loading...</div>;
    if (!session) return <Login />;
    if (needsPassword) return <SetPassword onComplete={async () => {
        // Mark as accepted now that they've set their password
        const now = new Date().toISOString();
        if (userId) {
            await masterSupabase.from("account_users")
                .update({ accepted_at: now, last_login_at: now })
                .eq("auth_user_id", userId);
        }
        setNeedsPassword(false);
        window.location.hash = "";
    }} />;
    if (error) return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", color: "#f87171", gap: 12 }}><span>Error: {error}</span><button onClick={() => masterSupabase.auth.signOut()} style={{ padding: "8px 16px", background: "none", border: "1px solid #333", borderRadius: 8, color: "#888", cursor: "pointer", fontFamily: "inherit" }}>Sign Out</button></div>;
    if (!account) return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", color: "#555", gap: 12 }}><span>No active account found.</span><button onClick={() => masterSupabase.auth.signOut()} style={{ padding: "8px 16px", background: "none", border: "1px solid #333", borderRadius: 8, color: "#888", cursor: "pointer", fontFamily: "inherit" }}>Sign Out</button></div>;

    return (
        <UserRoleContext.Provider value={{ role: userRole, userId }}>
            <Routes>
                <Route path="/" element={<RequirePerm perm="viewContent"><Overview /></RequirePerm>} />
                <Route path="/documents" element={<RequirePerm perm="viewContent"><Documents /></RequirePerm>} />
                <Route path="/locations" element={<RequirePerm perm="viewContent"><Locations /></RequirePerm>} />
                <Route path="/products" element={<RequirePerm perm="viewContent"><Products /></RequirePerm>} />
                <Route path="/conversations" element={<RequirePerm perm="viewConversations"><Conversations /></RequirePerm>} />
                <Route path="/leads" element={<RequirePerm perm="viewConversations"><Leads /></RequirePerm>} />
                <Route path="/widget" element={<Widget />} />
                <Route path="/training" element={<RequirePerm perm="trainAssistant"><Training /></RequirePerm>} />
                <Route path="/analytics" element={<RequirePerm perm="viewAnalytics"><Analytics /></RequirePerm>} />
                <Route path="/reports" element={<RequirePerm perm="viewAnalytics"><Reports /></RequirePerm>} />
                <Route path="/agents" element={<RequirePerm perm="viewAnalytics"><Agents /></RequirePerm>} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/team" element={<Team />} />
                <Route path="/document-widget" element={<DocumentWidget />} />
                <Route path="/api-docs" element={<ApiDocs />} />
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
        </UserRoleContext.Provider>
    );
}

// Top-level router — resolves custom domain, floor access bypasses admin auth
export default function App() {
    const [domainReady, setDomainReady] = useState(false);
    const [domainInfo, setDomainInfo] = useState(null);
    const isCustom = isCustomDomain();

    useEffect(() => {
        if (isCustom) {
            resolveCustomDomain().then((info) => {
                setDomainInfo(info);
                setDomainReady(true);
            });
        } else {
            setDomainReady(true);
        }
    }, []);

    if (!domainReady) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "#555" }}>Loading...</div>;

    // If custom domain doesn't resolve to any account, show error
    if (isCustom && !domainInfo) {
        return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", color: "#555", gap: 12 }}>
            <span style={{ fontSize: 16 }}>This domain is not configured.</span>
            <span style={{ fontSize: 12, color: "#444" }}>If you believe this is an error, contact your administrator.</span>
        </div>;
    }

    return (
        <DomainContext.Provider value={{ isCustom, domainInfo }}>
            <BrowserRouter>
                <Routes>
                    <Route path="/floor" element={<FloorAccess />} />
                    <Route path="/*" element={<AdminApp />} />
                </Routes>
            </BrowserRouter>
        </DomainContext.Provider>
    );
}
